import {Component,createEffect,createSignal,For,onMount, onCleanup, Show } from 'solid-js';
import {CMS} from './cms';
import {toQueryString,getPageRange,getPageBlockStart,getOrSetAsync, cleanImagePath } from './API';
import { ArtworksGrid } from './ArtworksGrid';
import { PageList } from './Controls';
import { ArtworksBlocks } from './ArtworksBlocks';
import { createStore } from 'solid-js/store';
import { Artwork } from './Types';

type OrderBy = string | "ByTitle" | "ByDate" | "ByMedium" | "ByRandom" | "ByArtist" | "ByDateAdded";
type QueryState = "NotStarted" | "Loading" | "Success" | "Error";
type Layout = "Grid" | "Block" | "Map";
type InfoWindowArgs = {
    name: string;
    artworks: any[];
};
function InfoWindow (p: InfoWindowArgs) {
  return (
    <>
        <div style="text-align: left; color: #333; max-width: 750px;">
            <h4>{p.name}</h4>
            <For each={p.artworks}>{(x: any) =>
                <div style="display: inline-block; padding: 5px; background-color: #333; text-align: center;">
                    <a href={ "/Artwork/Index/" + x.ArtworkId } target="_blank">
                        <img style="width: 100px; height: 100px;" src={ cleanImagePath(x.PrimaryImageUrl) + "?mode=crop&format=jpg&w=100&h=100" } />
                    </a>
                </div>
            }</For>
        </div>
    </>
  );      
};
type ListItem = {
    Text: string;
    Value: string;
}
type Props = {
    artworkLocationId: number;
    resources: {
        artworkTypes: ListItem[];
        countries: ListItem[];
        yesNos: ListItem[];
    };
}
type Resources = {
    types: ListItem[];
    countries: ListItem[];
    yesNos: ListItem[];
    imageSizes: ListItem[];
    orderBy: ListItem[];
}
type Filter = {
    artistId: string;
    artworkLocationId: string;
    startsWith: string;
    firstName: string;
    lastName: string;
    medium: string;
    country: string;
    title: string;
    type: string;
    imageSize: string;
    startYear: string;
    endYear: string;
    page: number;
    pageRows: number;
    hasDepictedAddress: string;
    order: string;
    direction: string;
}
export const ArtworksTab : Component<any> = (props) => {
    const [filter, setFilter] = createStore<Filter>({
        artistId: "",
        title: "",
        firstName: "",
        lastName: "",
        medium: "",
        startYear: "",
        endYear: "",
        artworkLocationId: props.artworkLocationId,
        page: 1,
        order: "ByArtist",
        direction: "Ascending"
    } as Filter);
    const [page, setPage] = createSignal<number>(1);
    const [queryState, setQueryState] = createSignal<QueryState>("NotStarted");
    const [layout, setLayout] = createSignal<Layout>("Grid");
    const [artworks, setArtworks] = createSignal<Artwork[]>([]);
    const [pages, setPages] = createSignal<number[]>([1]);
    const [letters, setLetters] = createSignal<string[]>([]);
    const [maxPage, setMaxPage] = createSignal<number>(1);
    const [titleChars, setTitleChars] = createSignal<string[]>([]);
    const [list, setList] = createStore<Resources>({
        types: props.resources.artworkTypes,
        countries: props.resources.countries,
        yesNos: props.resources.yesNos,
        imageSizes: [
            {Text:"", Value: ""},
            {Text:"Regular (up to 2 MPs)", Value: "M"},
            {Text:"Large (between 2 - 8 MPs)", Value: "L"},
            {Text:"Extra Large (between 8 - 16 MPs)", Value: "XL"},
            {Text:"Extremely Large (more than 16 MPs)", Value: "XXL"}
        ],
        orderBy: [
            { Text: "By default", Value: "ByDefault" },
            { Text: "By title", Value: "ByTitle" },
            { Text: "By date", Value: "ByDate" },
            { Text: "By medium", Value: "ByMedium" },
            { Text: "By artist", Value: "ByArtist" },
            { Text: "By date added", Value: "ByDateAdded" },
            { Text: "By random artworks", Value: "ByRandom" }
        ]
    });

    const resetFields = async () => {
        setFilter("country", "");
        setFilter("title", "");
        setFilter("type", "");
        setFilter("firstName", "");
        setFilter("lastName", "");
        setFilter("medium", "");
        setFilter("startsWith", "");
        setFilter("startYear", "");
        setFilter("endYear", "");
        setFilter("imageSize", "");
        setFilter("hasDepictedAddress", "");
        setFilter("order", "ByArtist");
        setFilter("direction", "Ascending");
        setFilter("page", 1);        
        await search();
    };
    const pageBlockSize = 20;
    const skipToPreviousBlock = async () =>{
        const pageBlockStart = getPageBlockStart(filter.page, pageBlockSize);
        if (pageBlockStart - pageBlockSize >= 1) {
            const previousPageBlockStart = pageBlockStart - pageBlockSize;
            setFilter("page", previousPageBlockStart);
            await search();
        }
    };
    const skipToNextBlock = async () =>{
        const pageBlockStart = getPageBlockStart(filter.page, pageBlockSize);
        if (pageBlockStart + pageBlockSize <= maxPage()) {
            const nextPageBlockStart = pageBlockStart + pageBlockSize;
            setFilter("page", nextPageBlockStart);
            await search();
        }
    };
    const searchByStartLetter = async (letter: string) => {
        setFilter("startsWith", letter == filter.startsWith ? "" : letter);
        setFilter("page", 1);
        await search();
    };
    const searchByPage = async (page: number) => {
        setFilter("page", page);
        await search();
    }
    const submit = async (e: Event) => {
        e.preventDefault();
        await search();
    };
    const retrySearch = async () => {
        await search();
    };
    const getFilter = () => {
        const parameters = {
            ArtistId: filter.artistId || "",
            ArtworkLocationId: filter.artworkLocationId,
            Title: filter.title || "",
            FirstName: filter.firstName || "",
            LastName: filter.lastName || "",
            Medium: filter.medium || "",
            Country: filter.country || "",
            ArtworkType: filter.type || "",
            FromYear: filter.startYear || "",
            ToYear: filter.endYear || "",
            StartsWith: filter.startsWith || "",
            ImageSize: filter.imageSize || "",
            HasDepictedAddress: filter.hasDepictedAddress || "",
            Page: filter.page,
            Order: filter.order || "",
            Direction: filter.direction || ""
        };
        return parameters;
    };
    const getData = async () => {
        const filter = toQueryString(getFilter());
        const json = await getOrSetAsync("artworks/" + filter, async () => {
            const response = await fetch("/Artist/SolidSearchByArtworkLocationJson?" + filter);
            const json = await response.json();
            return json;
        });
        return json;
    };
    const search = async (reset = true) => {
        setQueryState("Loading");
        const json = await getData();
        if (!json.Success) {
            setQueryState("Error");
            return;
        }
        setQueryState("Success");
        const results: Artwork[] = json.Data.Items.map((item: any) => {
            const result: Artwork = {
                id: item.Id,
                artist: {
                    id: item.Artist.Id,
                    slug: item.Artist.Slug,
                    firstName: item.Artist.FirstName,
                    lastName: item.Artist.LastName
                },
                backgroundColor: item.BackgroundColor,
                title: item.Title,
                alternateTitle: item.AlternateTitle,
                translateTitle: item.TranslatedTitle,
                medium: item.Medium,
                credit: item.Credit,
                content: item.Content,
                imageCount: item.ImageCount,
                measurements: item.Measurements,
                showHiresIcon: item.ShowHiresIcon,
                imageHeight: item.ImageHeight || 0,
                imageWidth: item.ImageWidth || 0,
                isForSale: item.IsForSale,
                isSold: item.IsSold,
                url: `/artworks/${item.Artist.Slug}/${item.Slug}/${item.Id}`,
                artworkLocation: !!item.ArtCollection ? {
                    id: item.ArtCollection.Id,
                    url: `/art-collections/${item.ArtCollection.Slug}/${item.ArtCollection.Id}`,
                    name: item.ArtCollection.Name,
                    details: [item.ArtCollection.City, item.ArtCollection.Country].filter(x => !!x).join(" | "),
                } : undefined,
                primaryImageUrl: item.ImageUrl,
                dateAdded: item.DateAdded,
                dateRange: item.DateRange,
                salonSummaries: item.SalonSummaries.map((x: any) => ({
                    "name": x.Name,
                    "area": x.Area,
                    "salonArtworkId": x.SalonArtworkId,
                    "results": x.Results
                }))
            };
            return result;
        });
        const _results = reset ? results : artworks().concat(results);
        setArtworks(_results);
        setPage(json.Data.Page);
        setMaxPage(json.Data.MaxPage);
        setTitleChars(json.Data.Titles);
        const pageRange = getPageRange(filter.page, pageBlockSize, maxPage());
        setPages(pageRange);
        return json;
    };
    const blockIconColour = () => layout() === "Block" ? "red" : "#000";
    const gridIconColour = () => layout() === "Grid" ? "red" : "#000";
    const loadNextPage = async () => {
        if (filter.page >= maxPage()) {
            return;
        }
        setFilter("page", filter.page + 1);
        await search(false);
    };
    const createAlphabet = () => {
        const alpha = Array.from(Array(26)).map((e, i) => i + 65);
        const alphabet = alpha.map((x) => String.fromCharCode(x));
        return alphabet;
    };
   
    
    const offset = () => layout() == "Grid" ? 1000 : layout() == "Block" ? 1250 : 500;
    const handleScroll = async () => {
        if (window.innerHeight + window.scrollY >= document.body.offsetHeight - offset()) {
            if (queryState() != "Loading") {
                await loadNextPage();
            }
        }
    };
    createEffect(async () => {
        window.addEventListener('scroll', handleScroll);
    });
    onCleanup(async () => {
        window.removeEventListener('scroll', handleScroll);
    });
    onMount(async () => {
        setLetters(createAlphabet());
        await search();
    });
return (
<div style="background-color: #fff;">   
    <form onSubmit={submit}>
        <section class="search" style="border: none; padding-bottom: 20px;">
            <div class="row">
                <div class="col-md-1">
                    <label>
                        <CMS id="label::title">First Name</CMS>
                    </label>
                    <input
                        type="text"
                        class="form-control"
                        value={filter.firstName}
                        onInput={(e) => setFilter("firstName", e.currentTarget.value)}
                    />
                </div>
                <div class="col-md-1">
                    <label>
                        <CMS id="label::title">Last Name</CMS>
                    </label>
                    <input
                        type="text"
                        class="form-control"
                        value={filter.lastName}
                        onInput={(e) => setFilter("lastName", e.currentTarget.value)}
                    />
                </div>
                <div class="col-md-2">
                    <label>
                        <CMS id="label::title">Title</CMS>
                    </label>
                    <input
                        type="text"
                        class="form-control"
                        value={filter.title}
                        onInput={(e) => setFilter("title", e.currentTarget.value)}
                    />
                </div>
                <div class="col-md-2">
                    <label>
                        <CMS id="label::medium">Medium</CMS>
                    </label>
                    <input
                        type="text"
                        class="form-control"
                        value={filter.medium}
                        onInput={(e) => setFilter("medium", e.currentTarget.value)}
                    />
                </div>
                <div class="col-md-2">
                    <label>
                        <CMS id="label::type">Type</CMS>
                    </label>
                    <select class="form-control" value={filter.type} onInput={(e) => setFilter("type", e.currentTarget.value)}>
                        <For each={list.types}>{(item) =>
                            <option value={item.Value}>{item.Text}</option>
                        }</For>
                    </select>
                </div>
                <div class="col-md-2">
                    <label>
                        <CMS id="label::country">Country</CMS>
                    </label>
                    <select class="form-control" value={filter.country} onInput={(e) => setFilter("country", e.currentTarget.value)}>
                        <For each={list.countries}>{(item) =>
                            <option value={item.Value}>{item.Text}</option>
                        }</For>
                    </select>
                </div>
                <div class="col-md-2">
                    <label>
                        <CMS id="label::sort-by">Sort by</CMS>
                    </label>
                    <select class="form-control" value={filter.order} onInput={(e) => setFilter("order", e.currentTarget.value)}>
                        <For each={list.orderBy}>{(item) =>
                            <option value={item.Value}>{item.Text}</option>
                        }</For>
                    </select>
                </div>
            </div>
            <div class="row" style="margin-top: 10px;">
                <div class="col-md-2">
                    <div class="row">
                        <div class="col-md-6">
                            <label><CMS id="label::from-year">From year</CMS></label>
                            <input
                                type="text"
                                class="form-control"
                                value={filter.startYear}
                                onInput={(e) => setFilter("startYear", e.currentTarget.value)}
                            />
                        </div>
                        <div class="col-md-6">
                            <label><CMS id="label::to-year">To year</CMS></label>
                            <input
                                type="text"
                                class="form-control"
                                value={filter.endYear}
                                onInput={(e) => setFilter("endYear", e.currentTarget.value)}
                            />
                        </div>
                    </div>
                    <div class="row" style="margin-top: 10px;">
                        <div class="col-md-12">
                            <button style="margin-right: 5px;" type="button" class="btn btn-default" onClick={(e) => { e.preventDefault(); resetFields(); }}><CMS id="button::clear">Clear</CMS></button>
                            <button type="button" class="btn btn-primary" onClick={(e) => { e.preventDefault(); search(); }}><CMS id="button::search">Search</CMS></button>
                        </div>
                    </div>
                </div>
                <div class="col-md-1">
                    <div class="check">
                        <label>
                            Image size
                        </label>
                        <select class="form-control" value={filter.imageSize} onInput={(e) => setFilter("imageSize", e.currentTarget.value)}>
                            <For each={list.imageSizes}>{(item) =>
                                <option value={item.Value}>{item.Text}</option>
                            }</For>
                        </select>
                    </div>
                </div>
                <div class="col-md-1">
                    <div class="check">
                        <label>
                            Depicts a place
                        </label>
                        <select class="form-control" value={filter.hasDepictedAddress} onInput={(e) => setFilter("hasDepictedAddress", e.currentTarget.value)}>
                        <For each={list.yesNos}>{(item) =>
                            <option value={item.Value}>{item.Text}</option>
                        }</For>
                        </select>
                    </div>
                </div>
                <div class="col-md-4">
                    <label><CMS id="label::titles-starting-with">Titles starting with</CMS></label>
                    <div>
                        {/* <AlphabeticalList letter={filter.startsWith} existingLetters={titleChars} onClick={searchByStartLetter}></AlphabeticalList> */}
                    </div>
                    <label style="margin-top: 10px;"><CMS id="label::page">Page</CMS></label>
                    <div>
                        <span class="alphabet-index" onClick={() => skipToPreviousBlock() }><span class="fa fa-chevron-left"></span></span>
                        <PageList page={page} pages={pages} onClick={searchByPage}></PageList>
                        <span class="alphabet-index" onClick={() => skipToNextBlock() }><span class="fa fa-chevron-right"></span></span>
                    </div>
                </div>
                <div class="col-md-2">
                    <label>
                        <CMS id="label::view">View</CMS>
                    </label>
                    <div>
                        <div style="float: left; margin-right: 20px;" onClick={(e) => setLayout("Grid")}>
                            <div>
                                <span class="glyphicon glyphicon-th-large" style={{ "font-size": "2em", "color": gridIconColour() }}></span>
                            </div>
                            <div style={{ "font-family": "Oswald", "font-size": "1em", "letter-spacing": "0.1em", "color": gridIconColour() }}>
                                <CMS id="label::grid">Grid</CMS>
                            </div>
                        </div>
                        <div style="float: left; margin-right: 20px;" onClick={(e) => setLayout("Block")}>
                            <div>
                                <span class="glyphicon glyphicon-th" style={{ "font-size": "2em", "color": blockIconColour() }}></span>
                            </div>
                            <div style={{ "font-family": "Oswald", "font-size": "1em", "letter-spacing": "0.1em", "color": blockIconColour() }}>
                                <CMS id="label::block">Block</CMS>
                            </div>
                        </div>
                    </div>
                </div>
            </div>
        </section>
    </form>
    <section class="salon-results">
        <div style="background-color: #333; position: relative; text-align: center;">
            <Show when={queryState() == "Success" && artworks().length === 0}>
                <div class="finalist-grid">
                    <div style="text-align: center;">
                        <h2 style="font-size: 3em; color: #fff; padding: 40px; font-family: 'Oswald';">
                            <CMS id="message::search-returned-no-results">This search returned no results.</CMS>
                        </h2>
                    </div>
                </div>
            </Show>
            <Show when={queryState() == "Error"}>
                <div class="finalist-grid">
                    <div style="text-align: center;">
                        <div style="padding: 40px;">
                            <h2 style="font-size: 3em; color: #fff; font-family: 'Oswald';">
                                <CMS id="message::unexpected-search-error">There was an unexpected error. Please try again.</CMS>
                            </h2>
                            <div style="font-size: 2em; margin-top: 30px;">
                                <button type="button" onClick={retrySearch}><span class="fa fa-refresh"></span> <CMS id="button::retry">Retry</CMS></button>
                            </div>
                        </div>
                    </div>
                </div>
            </Show>
            <Show when={layout() === "Grid"}>
                <ArtworksGrid session={props.session} artworks={artworks}></ArtworksGrid>                       
            </Show>
            <Show when={layout() === "Block"}>
                <ArtworksBlocks artworks={artworks}></ArtworksBlocks>
            </Show>
        </div>
    </section>
</div>);
}